/**
 * Client-side scheduling-eligibility predicate — a direct port of
 * seedz.manage.service.scheduling.SchedulingEligibilityService
 * .isSchedulingEnabled: true iff the project's schema carries a non-archived
 * EFFORT_MD field AND at least one non-archived OWNER or DEVELOPER field.
 * Reuses whatever fields[] the caller already fetched via useBacklogFields —
 * no new network call from useSchedulingEligible.
 */
import { useBacklogFields } from './backlogField';
import type { BacklogFieldKind, BacklogSystemField } from '@/types/backlogField';

/** Structural subset of BacklogField this predicate actually needs — narrow enough that
 *  both a real BacklogField[] (CrBacklogTable) and the schema builder's mode-agnostic
 *  BuilderField[] (AddColumnControl, draft + live) are assignable without a cast. */
export interface SchedulingEligibilityField {
  kind: BacklogFieldKind;
  systemRef: BacklogSystemField | null;
  archived: boolean;
}

function hasActive(fields: SchedulingEligibilityField[], ref: BacklogSystemField): boolean {
  return fields.some((f) => f.kind === 'SYSTEM' && f.systemRef === ref && !f.archived);
}

export function isSchedulingEligible(fields: SchedulingEligibilityField[]): boolean {
  if (!hasActive(fields, 'EFFORT_MD')) return false;
  return hasActive(fields, 'OWNER') || hasActive(fields, 'DEVELOPER');
}

export function useSchedulingEligible(projectId: string) {
  const { data: fields = [], isLoading } = useBacklogFields(projectId);
  return { eligible: isSchedulingEligible(fields), isLoading };
}
